import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRANCH_LABELS } from '@lorsamthai/ops-shared';
import {
  listTransferOrders,
  receiveTransfer,
  shipTransfer,
  type TransferOrderDoc,
} from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtDate, fmtInt } from '../lib/format.js';
import { useAuth } from '../auth/context.js';

const STATUS_TONE: Record<string, 'info' | 'warn' | 'ok' | 'neutral'> = {
  approved: 'info',
  in_transit: 'warn',
  received: 'ok',
  cancelled: 'neutral',
};

export default function TransferList() {
  const { me } = useAuth();
  const [items, setItems] = useState<TransferOrderDoc[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listTransferOrders();
      setItems(r.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const ship = async (id: number) => {
    try { await shipTransfer(id); await load(); }
    catch (err) { alert(err instanceof Error ? err.message : 'failed'); }
  };
  const receive = async (id: number) => {
    try { await receiveTransfer(id); await load(); }
    catch (err) { alert(err instanceof Error ? err.message : 'failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">การโอนระหว่างสาขา</h1>
        {me?.role === 'super_admin' && (
          <Link to="/transfers/new"
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white">
            + สร้างโอน
          </Link>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">TR</th>
              <th className="px-3 py-2">จาก → ไป</th>
              <th className="px-3 py-2">สถานะ</th>
              <th className="px-3 py-2 text-right">เส้น</th>
              <th className="px-3 py-2">สร้าง</th>
              <th className="px-3 py-2">รายการ</th>
              <th className="px-3 py-2">การกระทำ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">ยังไม่มี transfer</td></tr>
            )}
            {items.map((tr) => {
              const total = tr.transfer_order_items.reduce((s, i) => s + i.qty, 0);
              const canShip =
                (me?.role === 'super_admin' || me?.branch === tr.from_branch) &&
                tr.status === 'approved';
              const canReceive =
                (me?.role === 'super_admin' || me?.branch === tr.to_branch) &&
                ['approved', 'in_transit'].includes(tr.status);
              return (
                <tr key={tr.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{tr.tr_ref ?? `#${tr.id}`}</td>
                  <td className="px-3 py-2 text-xs">
                    {BRANCH_LABELS[tr.from_branch]} → {BRANCH_LABELS[tr.to_branch]}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={STATUS_TONE[tr.status] ?? 'neutral'}>{tr.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(total)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(tr.created_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    {tr.transfer_order_items.slice(0, 2).map((i) => (
                      <div key={i.sku} className="font-mono">{i.sku} × {i.qty}</div>
                    ))}
                    {tr.transfer_order_items.length > 2 && (
                      <span className="text-slate-400">+{tr.transfer_order_items.length - 2} รายการ</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {canShip && (
                        <button onClick={() => ship(tr.id)}
                          className="rounded border border-slate-200 px-2 py-0.5 text-xs hover:bg-slate-50">
                          จัดของแล้ว
                        </button>
                      )}
                      {canReceive && (
                        <button onClick={() => receive(tr.id)}
                          className="rounded bg-emerald-600 px-2 py-0.5 text-xs text-white hover:bg-emerald-700">
                          รับแล้ว
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
